import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { useLang } from "@/i18n/LanguageContext";

const STORAGE_KEY = "ak_favorites";
const META_KEY = "ak_fav_meta";

const FavoritesContext = createContext(null);

export const useFavorites = () => {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error("useFavorites must be used within FavoritesProvider");
  return ctx;
};

const loadJSON = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

export const FavoritesProvider = ({ children }) => {
  const { t } = useLang();
  const [favorites, setFavorites] = useState(() => loadJSON(STORAGE_KEY, []));
  const [meta, setMetaState] = useState(() => loadJSON(META_KEY, {}));
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
  }, [favorites]);

  useEffect(() => {
    localStorage.setItem(META_KEY, JSON.stringify(meta));
  }, [meta]);

  const isFav = useCallback((id) => favorites.includes(id), [favorites]);

  const toggle = useCallback(
    (id) => {
      setFavorites((prev) => {
        if (prev.includes(id)) {
          toast(t("favorites.removed"));
          return prev.filter((x) => x !== id);
        }
        toast.success(t("favorites.added"));
        return [...prev, id];
      });
    },
    [t],
  );

  const remove = useCallback((id) => {
    setFavorites((prev) => prev.filter((x) => x !== id));
    setMetaState((prev) => {
      const { [id]: _drop, ...rest } = prev;
      return rest;
    });
  }, []);
  const clear = useCallback(() => {
    setFavorites([]);
    setMetaState({});
  }, []);
  const setMeta = useCallback((id, patch) => {
    setMetaState((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));
  }, []);
  const openPanel = useCallback(() => setPanelOpen(true), []);
  const closePanel = useCallback(() => setPanelOpen(false), []);

  return (
    <FavoritesContext.Provider
      value={{ favorites, count: favorites.length, isFav, toggle, remove, clear, meta, setMeta, panelOpen, openPanel, closePanel }}
    >
      {children}
    </FavoritesContext.Provider>
  );
};
